import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { User } from '../../shared/models/user';
import { AccountService } from '../../core/services/account.service';
import { NgxPermissionsModule } from 'ngx-permissions';
import { Subscription, filter } from 'rxjs';
import { UsersService } from '../../core/services/users.service';
import { BRANDING } from '../../core/constants/branding.constants';
import { SystemNotificationCenterService } from '../../core/services/system-notification-center.service';
import { BuildVersionComponent } from '../../shared/components/build-version/build-version.component';

interface Link {
  name: string;
  path?: string;
  icon?: string;
  isFontAwesome?: boolean;
  hasChildren: boolean;
  permissions?: string[];
  children?: Link[];
}

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, RouterModule, MatIconModule, NgxPermissionsModule, BuildVersionComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, OnDestroy {
  branding = BRANDING;
  user: User | null = null;
  expandedItems: { [key: string]: boolean } = {};
  private routerSubscription: Subscription | null = null;
  private unreadCountSubscription: Subscription | null = null;
  unreadCount = 0;

  links: Link[] = [
    {
      name: 'Página Inicial',
      path: '/dash',
      icon: 'fa-solid fa-house',
      isFontAwesome: true,
      hasChildren: false,
    },
    {
      name: 'Consultores',
      icon: 'fa-solid fa-people-group',
      hasChildren: true,
      isFontAwesome: true,
      permissions: ['Permissions.Consultants.List'],
      children: [
        {
          name: 'Lista de Consultores',
          path: '/consultores',
          hasChildren: false,
          isFontAwesome: false,
          permissions: ['Permissions.Consultants.List'],
        },
      ],
    },
    {
      name: 'Roteirização',
      icon: 'fa-solid fa-route',
      hasChildren: true,
      isFontAwesome: true,
      children: [
        {
          name: 'Listagem de Roteiros',
          path: '/roteirizacao',
          hasChildren: false,
          isFontAwesome: false,
        },
        {
          name: 'Ciclos de Visitação',
          path: '/ciclos-visita',
          hasChildren: false,
          isFontAwesome: false,
        }
      ],
    },
    {
      name: 'Formulários',
      path: '/formularios',
      icon: 'fa-solid fa-file-invoice',
      hasChildren: false,
      isFontAwesome: true,
    },
    {
      name: 'Notificações',
      icon: 'fa-solid fa-bell',
      hasChildren: true,
      isFontAwesome: true,
      children: [
        {
          name: 'Notificações Push',
          path: '/notificacoes/push',
          hasChildren: false,
          isFontAwesome: false,
          // permissions: ['PushNotifications.List'],
        },
        {
          name: 'Notificações In-App',
          path: '/notificacoes/in-app',
          hasChildren: false,
          isFontAwesome: false,
          // permissions: ['InAppNotifications.List'],
        },
      ],
    },
    {
      name: 'Relatórios',
      icon: 'fa-solid fa-chart-simple',
      hasChildren: true,
      isFontAwesome: true,
      children: [
        {
          name: 'Relatório de Produtividade',
          path: '/relatorios/produtividade',
          hasChildren: false,
          isFontAwesome: false,
        },
        {
          name: 'Relatório de Execução',
          path: '/relatorios/execucao',
          hasChildren: false,
          isFontAwesome: false,
        },
        {
          name: 'Relatório de Roteirização',
          path: '/relatorios/roteirizacao',
          hasChildren: false,
          isFontAwesome: false,
        },
        {
          name: 'Relatório de Visitação',
          path: '/relatorios/visitacao',
          hasChildren: false,
          isFontAwesome: false,
        },
      ],
    },
    {
      name: 'Administração',
      icon: 'fa-solid fa-user-shield',
      hasChildren: true,
      isFontAwesome: true,
      children: [
        {
          name: 'Lista de Administradores',
          path: '/administracao',
          hasChildren: false,
          isFontAwesome: false,
        },
        {
          name: 'Permissões do Sistema',
          path: '/cargos',
          hasChildren: false,
          isFontAwesome: false,
        },
      ],
    },
    {
      name: 'Configurações',
      icon: 'fa-solid fa-gear',
      hasChildren: true,
      isFontAwesome: true,
      children: [
        {
          name: 'Configurações do App',
          path: '/configuracoes-app',
          hasChildren: false,
          isFontAwesome: false,
        },
        {
          name: 'Configurações do Sistema',
          path: '/configuracoes',
          hasChildren: false,
          isFontAwesome: false,
        },
      ],
    },
  ];

  constructor(
    private router: Router,
    private accountService: AccountService,
    private userService: UsersService,
    private notificationCenterService: SystemNotificationCenterService,
  ) { }

  ngOnInit() {
    this.notificationCenterService.initialize();
    this.unreadCountSubscription = this.notificationCenterService.unreadCount$.subscribe((count) => {
      this.unreadCount = count;
    });

    this.user = this.userService.currentUser();
    const initializeUser = (user: User) => {
      this.user = user;
    };

    if (!this.user) {
      this.userService.getUserInfo().subscribe({ next: initializeUser });
    } else {
      initializeUser(this.user);
    }

    this.checkDashboardRoutes(this.router.url);

    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.checkDashboardRoutes(event.url);
    });
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    if (this.unreadCountSubscription) {
      this.unreadCountSubscription.unsubscribe();
    }
  }

  get unreadBadgeLabel(): string {
    if (this.unreadCount > 99) {
      return '99+';
    }

    return String(this.unreadCount);
  }

  checkDashboardRoutes(url: string) {
    this.links.filter(link => link.hasChildren && link.children).forEach(parentLink => {
      const isChildRoute = parentLink.children!.some((child: Link) =>
        child.path && url.includes(child.path)
      );
      if (isChildRoute) {
        this.expandedItems[parentLink.name] = true;
      }
    });
  }

  toggleExpand(name: string): void {
    this.expandedItems[name] = !this.expandedItems[name];
  }

  isExpanded(name: string): boolean {
    return this.expandedItems[name] === true;
  }

  logout() {
    this.accountService.logout();
  }

  openNotificationCenter(): void {
    this.notificationCenterService.togglePanel();
  }
}
